import {
    CadNextRelatedDataLocationQuery,
    MapViewportEventView,
    MapViewportUnitView,
    SubPremiseDto,
    SummaryCategoryEnum,
    SummaryCategoryEnumType,
    SubdivisionOptionsBootstrapView,
    SubdivisionOption,
    WorkspaceMapDto,
    AgencyProfileOption,
} from '@mark43/cad-api';
import { replace } from 'lodash';
import {
    EsriMapEntityTypeEnum,
    EsriMapFilterOptions,
    EsriMapFilterState,
    DeprecatedEsriMapFilterOptions,
    DeprecatedEsriMapFilterState,
    EsriMapLayerOption,
    LayerId,
} from '../../esri/types';
import { LocationStoreView } from '../../location/types';
import { globalAttributes, AgencyTypeGlobal } from '../../common/global-attributes';
import { WorkspaceMapFilters } from '../../workspace-windows/types';

const buildSubPremiseDtos = (location: LocationStoreView) => {
    const subPremises: SubPremiseDto[] = [];

    for (let i = 1; i <= 5; i++) {
        const nameKey = `subPremise${i}Name` as keyof LocationStoreView;
        const valueKey = `subPremise${i}Value` as keyof LocationStoreView;

        if (location[nameKey] && location[valueKey]) {
            subPremises.push({
                subPremiseName: location[nameKey] as string,
                subPremiseValue: location[valueKey] as string,
            });
        }
    }

    return subPremises;
};

export const buildRelatedDataLocationQuery = (
    location: LocationStoreView
): CadNextRelatedDataLocationQuery => {
    const {
        country,
        crossStreet1,
        crossStreet2,
        locality,
        postalCode,
        rangeName,
        streetAddress,
        subPremise,
    } = location;
    return {
        country,
        crossStreet1,
        crossStreet2,
        locality,
        postalCode,
        rangeName,
        streetAddress,
        subPremise,
        subPremiseDtos: buildSubPremiseDtos(location),
    };
};

const PREFIXES_TO_OMIT = ['EMS', 'FIRE', 'EVENT'];

function isSubstringsInAString(searchString: string, substrings: string[]): boolean {
    return substrings.some((prefix) => searchString.includes(prefix));
}

function buildSummaryCategoriesFilterOption(): DeprecatedEsriMapFilterOptions['summaryCategoryOptions'] {
    const options = [];

    for (const key in SummaryCategoryEnum) {
        if (!isSubstringsInAString(key, PREFIXES_TO_OMIT)) {
            const value = SummaryCategoryEnum[key as SummaryCategoryEnumType].name as string;

            options.push({
                label: replace(value, /_|UNIT/g, ' ').trim(),
                value,
            });
        }
    }

    return options;
}

export const SUMMARY_FILTER_CATEGORIES = buildSummaryCategoriesFilterOption();

const AGENCY_TYPE_GLOBAL_ATTR_ID_TO_PRIMARY_SUBDIVISION_NAME_MAP = {
    [globalAttributes.agencyTypeGlobal.POLICE]: 'primaryPoliceSubdivisionName',
    [globalAttributes.agencyTypeGlobal.FIRE]: 'primaryFireSubdivisionName',
    [globalAttributes.agencyTypeGlobal.EMS]: 'primaryEmsSubdivisionName',
} as const;
const AGENCY_TYPE_GLOBAL_ATTR_ID_TO_AGENCY_TYPE_SUBDIVISION_MAP = {
    [globalAttributes.agencyTypeGlobal.POLICE]: 'policeSubdivision',
    [globalAttributes.agencyTypeGlobal.FIRE]: 'fireSubdivision',
    [globalAttributes.agencyTypeGlobal.EMS]: 'emsSubdivision',
} as const;

export type PrimarySubdivisionNames = {
    [key: string]: string | undefined;
    primaryPoliceSubdivisionName?: string;
    primaryFireSubdivisionName?: string;
    primaryEmsSubdivisionName?: string;
};

export const getPrimarySubdivisionNameKeyByAgencyType = (
    agencyTypeGlobalAttrId?: AgencyTypeGlobal
): string => {
    return agencyTypeGlobalAttrId
        ? AGENCY_TYPE_GLOBAL_ATTR_ID_TO_PRIMARY_SUBDIVISION_NAME_MAP[agencyTypeGlobalAttrId] ?? ''
        : '';
};

export const getPrimarySubdivisionOptions = ({
    subdivisionOptionsBootstrapView,
    agencyTypeGlobalAttrId,
    primarySubdivisionNames,
}: {
    subdivisionOptionsBootstrapView?: SubdivisionOptionsBootstrapView;
    agencyTypeGlobalAttrId?: AgencyTypeGlobal;
    primarySubdivisionNames?: PrimarySubdivisionNames;
}): SubdivisionOption[] => {
    if (!subdivisionOptionsBootstrapView || !agencyTypeGlobalAttrId || !primarySubdivisionNames) {
        return [];
    }
    const primarySubdivisionName =
        primarySubdivisionNames?.[getPrimarySubdivisionNameKeyByAgencyType(agencyTypeGlobalAttrId)];
    const agencySubdivision =
        AGENCY_TYPE_GLOBAL_ATTR_ID_TO_AGENCY_TYPE_SUBDIVISION_MAP[agencyTypeGlobalAttrId];

    if (!primarySubdivisionName || !agencySubdivision) {
        return [];
    }
    for (let i = 1; i <= 5; i++) {
        if (
            subdivisionOptionsBootstrapView[
                `${agencySubdivision}${i}Label` as keyof typeof subdivisionOptionsBootstrapView
            ] === primarySubdivisionName
        ) {
            return (subdivisionOptionsBootstrapView[
                `${agencySubdivision}${i}Options` as keyof typeof subdivisionOptionsBootstrapView
            ] ?? []) as SubdivisionOption[];
        }
    }
    return [];
};

const getPrimarySubdivisionOptionsFromAgencyView = ({
    subdivisionOptionsBootstrapView,
    agencySubdivisionPrefix,
}: {
    subdivisionOptionsBootstrapView: SubdivisionOptionsBootstrapView;
    agencySubdivisionPrefix: 'policeSubdivision' | 'fireSubdivision' | 'emsSubdivision';
}): SubdivisionOption[] => {
    // In the multi-agency path, the bootstrap view is already scoped per agency, so the
    // correct primary subdivision tier is the one whose options are marked
    // `isSubdivisionPrimary`. The single-agency helper uses the configured primary
    // subdivision name instead because it operates on the combined bootstrap view.
    for (let i = 1; i <= 5; i++) {
        const options = (subdivisionOptionsBootstrapView[
            `${agencySubdivisionPrefix}${i}Options` as keyof SubdivisionOptionsBootstrapView
        ] ?? []) as SubdivisionOption[];

        if (options.some((option) => option.isSubdivisionPrimary)) {
            return options;
        }
    }

    return [];
};

export const getMultiAgencyPrimarySubdivisionOptions = ({
    agencyOptions,
    agencyIdToSubdivisionOptionsBootstrapView,
    agencyTypeGlobalAttrId,
}: {
    agencyOptions: AgencyProfileOption[];
    agencyIdToSubdivisionOptionsBootstrapView?: Record<number, SubdivisionOptionsBootstrapView>;
    agencyTypeGlobalAttrId?: AgencyTypeGlobal;
}): SubdivisionOption[] => {
    if (!agencyTypeGlobalAttrId || !agencyIdToSubdivisionOptionsBootstrapView) {
        return [];
    }

    const agencySubdivision =
        AGENCY_TYPE_GLOBAL_ATTR_ID_TO_AGENCY_TYPE_SUBDIVISION_MAP[agencyTypeGlobalAttrId];

    if (!agencySubdivision) {
        return [];
    }

    return agencyOptions.flatMap((agencyOption) => {
        const subdivisionOptionsBootstrapView =
            agencyIdToSubdivisionOptionsBootstrapView[agencyOption.value];

        if (!subdivisionOptionsBootstrapView) {
            return [];
        }

        return getPrimarySubdivisionOptionsFromAgencyView({
            subdivisionOptionsBootstrapView,
            agencySubdivisionPrefix: agencySubdivision,
        }).map((option) => ({
            ...option,
            label: `${agencyOption.label}: ${option.label}`,
        }));
    });
};

export function filterViewportEntities({
    entityType,
    mapFilterState,
    viewportEntity,
    mapFilterOptions,
}: {
    entityType: EsriMapEntityTypeEnum;
    mapFilterState?: EsriMapFilterState;
    viewportEntity: MapViewportUnitView | MapViewportEventView;
    mapFilterOptions?: EsriMapFilterOptions;
}): boolean {
    if (!mapFilterState || !mapFilterOptions) {
        return true;
    }

    let isVisible = true;
    if (![EsriMapEntityTypeEnum.UNIT, EsriMapEntityTypeEnum.EVENT].includes(entityType)) {
        // only filter for UNIT and EVENT
        return isVisible;
    }

    if (isVisible && mapFilterState?.priorityFilters?.length) {
        isVisible =
            !!viewportEntity.priorityAttributeId &&
            mapFilterState.priorityFilters.includes(viewportEntity.priorityAttributeId);
    }

    if (isVisible && mapFilterState?.dispatchAreaFilters?.length) {
        isVisible =
            !!viewportEntity.dispatchAreaId &&
            mapFilterState.dispatchAreaFilters.includes(viewportEntity.dispatchAreaId);
    }

    if (isVisible && mapFilterState?.primarySubdivisionFilters?.length) {
        if (entityType === EsriMapEntityTypeEnum.UNIT) {
            const mapViewportUnitView = viewportEntity as MapViewportUnitView;
            // subdivision filter for unit will match assignedEventPrimarySubdivision or primarySubdivision
            isVisible =
                (!!mapViewportUnitView.assignedEventPrimarySubdivisionAttributeId &&
                    mapFilterState.primarySubdivisionFilters.includes(
                        mapViewportUnitView.assignedEventPrimarySubdivisionAttributeId
                    )) ||
                (!!mapViewportUnitView.primarySubdivisionAttributeId &&
                    mapFilterState.primarySubdivisionFilters.includes(
                        mapViewportUnitView.primarySubdivisionAttributeId
                    ));
            // also filter by unit's agency type
            if (isVisible && mapFilterOptions?.agencyTypeIdToDisplay) {
                isVisible =
                    mapViewportUnitView.unitClass === mapFilterOptions.agencyTypeIdToDisplay;
            }
        } else if (entityType === EsriMapEntityTypeEnum.EVENT) {
            isVisible =
                !!viewportEntity.primarySubdivisionAttributeId &&
                mapFilterState.primarySubdivisionFilters.includes(
                    viewportEntity.primarySubdivisionAttributeId
                );
        }
    }

    if (
        isVisible &&
        mapFilterState?.agencyTypeFilters?.length &&
        [EsriMapEntityTypeEnum.UNIT, EsriMapEntityTypeEnum.EVENT].includes(entityType)
    ) {
        isVisible = mapFilterState.agencyTypeFilters.includes(
            viewportEntity.agencyTypeGlobalAttrId
        );
    }

    if (
        isVisible &&
        entityType === EsriMapEntityTypeEnum.UNIT &&
        'mapUnitStatusOption' in viewportEntity
    ) {
        // Unit Status/summaryCategory only applies to UNIT
        if (mapFilterState?.mapUnitStatusFilters?.length) {
            isVisible = mapFilterState.mapUnitStatusFilters.includes(
                viewportEntity.mapUnitStatusOption.value
            );
        }
    }

    if (isVisible && mapFilterState?.agencyProfileFilters?.length) {
        isVisible =
            !!viewportEntity.agencyId &&
            mapFilterState.agencyProfileFilters.includes(viewportEntity.agencyId);
    }

    return isVisible;
}

/**
 * @deprecated map filter
 */
export function filterViewportEntitiesDeprecated({
    entityType,
    mapFilterState,
    viewportEntity,
    mapFilterOptions,
}: {
    entityType: EsriMapEntityTypeEnum;
    mapFilterState: DeprecatedEsriMapFilterState;
    viewportEntity: MapViewportUnitView | MapViewportEventView;
    mapFilterOptions?: DeprecatedEsriMapFilterOptions;
}): boolean {
    let isVisible = true;
    if (![EsriMapEntityTypeEnum.UNIT, EsriMapEntityTypeEnum.EVENT].includes(entityType)) {
        // only filter for UNIT and EVENT
        return isVisible;
    }

    if (isVisible && mapFilterState?.priorities?.length) {
        isVisible =
            !!viewportEntity.priority &&
            mapFilterState.priorities.includes(viewportEntity.priority);
    }

    if (isVisible && mapFilterState?.dispatchAreas?.length) {
        isVisible =
            !!viewportEntity.dispatchArea &&
            mapFilterState.dispatchAreas.includes(viewportEntity.dispatchArea);
    }

    if (isVisible && mapFilterState?.subdivisions?.length) {
        if (entityType === EsriMapEntityTypeEnum.UNIT) {
            const mapViewportUnitView = viewportEntity as MapViewportUnitView;
            // subdivision filter for unit will match assignedEventPrimarySubdivision or primarySubdivision
            isVisible =
                (!!mapViewportUnitView.assignedEventPrimarySubdivision &&
                    mapFilterState.subdivisions.includes(
                        mapViewportUnitView.assignedEventPrimarySubdivision
                    )) ||
                (!!mapViewportUnitView.primarySubdivision &&
                    mapFilterState.subdivisions.includes(mapViewportUnitView.primarySubdivision));
            // also filter by unit's agency type
            if (isVisible && mapFilterOptions?.agencyTypeIdToDisplay) {
                isVisible =
                    mapViewportUnitView.unitClass === mapFilterOptions.agencyTypeIdToDisplay;
            }
        } else if (entityType === EsriMapEntityTypeEnum.EVENT) {
            isVisible =
                !!viewportEntity.primarySubdivision &&
                mapFilterState.subdivisions.includes(viewportEntity.primarySubdivision);
        }
    }

    if (
        isVisible &&
        mapFilterState?.agencyTypesGlobalAttributeIds?.length &&
        [EsriMapEntityTypeEnum.UNIT, EsriMapEntityTypeEnum.EVENT].includes(entityType)
    ) {
        isVisible = mapFilterState.agencyTypesGlobalAttributeIds.includes(
            String(viewportEntity.agencyTypeGlobalAttrId)
        );
    }

    if (isVisible && entityType === EsriMapEntityTypeEnum.UNIT) {
        // summaryCategory/Unit Status only applies to UNIT
        if (mapFilterState?.summaryCategories?.length) {
            isVisible = isSubstringsInAString(
                viewportEntity.summaryCategory,
                mapFilterState.summaryCategories
            );
        }
    }

    return isVisible;
}

const initialMapFilterState: EsriMapFilterState = {
    agencyProfileFilters: [],
    agencyTypeFilters: [],
    dispatchAreaFilters: [],
    mapUnitStatusFilters: [],
    primarySubdivisionFilters: [],
    priorityFilters: [],
};

const initialWorkspaceMapFilters: Pick<
    WorkspaceMapDto,
    | 'agencyProfileFilters'
    | 'agencyTypeFilters'
    | 'dispatchAreaFilters'
    | 'mapUnitStatusFilters'
    | 'primarySubdivisionFilters'
    | 'priorityFilters'
> = {
    agencyProfileFilters: [],
    agencyTypeFilters: [],
    dispatchAreaFilters: [],
    mapUnitStatusFilters: [],
    primarySubdivisionFilters: [],
    priorityFilters: [],
};
const filterStateToFilterOptionsMapping = {
    agencyProfileFilters: 'agencyProfileOptions',
    agencyTypeFilters: 'agencyTypeOptions',
    dispatchAreaFilters: 'dispatchAreaOptions',
    mapUnitStatusFilters: 'unitStatusOptions',
    primarySubdivisionFilters: 'subdivisionOptions',
    priorityFilters: 'priorityOptions',
} as const;
type FilterStateKey = keyof typeof filterStateToFilterOptionsMapping;
type FilterOptionsKey = (typeof filterStateToFilterOptionsMapping)[FilterStateKey];

function entityIdsToFilterOptions(
    options: EsriMapFilterOptions[keyof EsriMapFilterOptions],
    entityIds: number[]
) {
    if (!options || typeof options === 'string') {
        return [];
    }
    return options
        .filter((option) => entityIds.includes(option.value))
        .map((option) => {
            return {
                value: option.value,
                label: option.label,
            };
        });
}

export function initializeMapFilterStateFromWorkspaceMapDto(
    workspaceMapDto?: WorkspaceMapDto
): EsriMapFilterState {
    if (!workspaceMapDto) {
        return { ...initialMapFilterState };
    }
    const res: EsriMapFilterState = {};
    Object.keys(filterStateToFilterOptionsMapping).forEach((stateKey) => {
        const typedStateKey = stateKey as FilterStateKey;
        res[typedStateKey] = workspaceMapDto[typedStateKey]?.map((option) => option.value) ?? [];
    });
    return res;
}

export function covertMapFiltersToWorkspaceMapFilters(
    mapFilterState: EsriMapFilterState,
    mapFilterOptions: EsriMapFilterOptions
): WorkspaceMapFilters {
    const workspaceMapFilters: WorkspaceMapFilters = { ...initialWorkspaceMapFilters };
    Object.entries(filterStateToFilterOptionsMapping).forEach(([stateKey, optionKey]) => {
        const typedStateKey = stateKey as FilterStateKey;
        const typedOptionKey = optionKey as FilterOptionsKey;

        const filterState = mapFilterState?.[typedStateKey];
        const filterOptions = mapFilterOptions?.[typedOptionKey];
        if (!filterOptions && workspaceMapFilters[typedStateKey]) {
            delete workspaceMapFilters[typedStateKey];
        } else {
            if (filterState && filterOptions?.length && workspaceMapFilters[typedStateKey]) {
                if (typedStateKey === 'primarySubdivisionFilters') {
                    workspaceMapFilters.primarySubdivisionFilters = entityIdsToFilterOptions(
                        filterOptions,
                        filterState
                    ) as SubdivisionOption[];
                } else if (typedStateKey === 'agencyProfileFilters') {
                    workspaceMapFilters.agencyProfileFilters = entityIdsToFilterOptions(
                        filterOptions,
                        filterState
                    ) as AgencyProfileOption[];
                } else {
                    workspaceMapFilters[typedStateKey] = entityIdsToFilterOptions(
                        filterOptions,
                        filterState
                    );
                }
            } else {
                workspaceMapFilters[typedStateKey] = [];
            }
        }
    });
    return workspaceMapFilters;
}

export function initializeMapLayerIdsFromWorkspaceMapDto(
    workspaceMapDto?: WorkspaceMapDto
): LayerId[] {
    return workspaceMapDto?.layerFilters.map((layerFilter) => layerFilter.value) ?? [];
}

export function convertMapLayerIdsToWorkspaceMapLayers(
    mapLayerIds: LayerId[]
): EsriMapLayerOption[] {
    // BE doesn't save map layer labels in DB.  map layer labels are configured and stored in esri portal.
    return mapLayerIds?.map((layerId) => ({ value: `${layerId}`, label: `${layerId}` })) ?? [];
}

/**
 * A department-scoped private webmap resolved via the OAuth token path takes precedence
 * over the department's default public webmap when both are present.
 */
export function resolveWebMapPortalId(
    identityWebMapPortalId: string | undefined,
    esriWebmapPortalId: string | undefined
): string | undefined {
    return identityWebMapPortalId ?? esriWebmapPortalId;
}
